import React, { useEffect, useMemo, useState } from "react";
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Divider,
  Drawer,
  Paper,
  Select,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandLess,
  ExpandMore,
  Groups as GroupsIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  Search as SearchIcon,
  ReportProblem as ReportProblemIcon,
} from "@mui/icons-material";
import { onAuthStateChanged, signOut } from "firebase/auth";
import {
  addDoc,
  collection,
  deleteDoc,
  documentId,
  doc,
  getDocs,
  onSnapshot,
  query,
  updateDoc,
  where,
} from "firebase/firestore";
import { auth, firestore } from "../../../firebase";
import logo from "../../../OV-WarrantyMS.png";
import { useNavigate } from "react-router-dom";
import {
  isComplaintClosed,
  normalizeComplaintStatus,
} from "../../../utils/complaintStatus";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Alert,
} from "@mui/material";

const ROLE_LABELS = {
  facility_admin: "Facility Admin",
  facility_operator: "Facility Operator",
  operator: "Facility Operator",
  seller: "Facility Operator",
  technician: "Technician",
};

const formatRole = (role) => {
  if (!role) return "-";
  const normalizedRole = String(role).trim().toLowerCase();
  return ROLE_LABELS[normalizedRole] || role;
};

const isSellerRole = (role) => formatRole(role) === "Facility Operator";
const isFacilityStaffRole = (role) => {
  const normalizedRole = formatRole(role);
  return normalizedRole === "Facility Operator" || normalizedRole === "Technician";
};

const formatCreatedAt = (createdAt) => {
  if (!createdAt) return "-";
  const dateValue = createdAt?.toDate ? createdAt.toDate() : new Date(createdAt);
  if (Number.isNaN(dateValue.getTime())) return "-";
  return dateValue.toLocaleDateString();
};

const formatDate = (value) => {
  if (!value) return "-";
  const resolvedDate = value?.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(resolvedDate.getTime())) return "-";
  return resolvedDate.toLocaleDateString();
};

const getComplaintStatusChipStyle = (status) => {
  const normalizedStatus = normalizeComplaintStatus(status);

  if (normalizedStatus === "Open") {
    return { bgcolor: "#e3f2fd", color: "#1565c0" };
  }

  if (normalizedStatus === "In Progress") {
    return { bgcolor: "#fff3e0", color: "#ef6c00" };
  }

  if (normalizedStatus === "Resolved") {
    return { bgcolor: "#e8f5e9", color: "#2e7d32" };
  }

  return { bgcolor: "#f5f5f5", color: "#616161" };
};

const FacilityAdminPage = () => {
  const navigate = useNavigate();
  const [tenantId, setTenantId] = useState("");
  const [currentFacilityId, setCurrentFacilityId] = useState("");
  const [facilityUsers, setFacilityUsers] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [profileLabel, setProfileLabel] = useState("F");
  const [profileEmail, setProfileEmail] = useState("");
  const [profileRole, setProfileRole] = useState("facility_admin");
  const [profileBranchName, setProfileBranchName] = useState("-");
  const [profileRegion, setProfileRegion] = useState("-");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [facilityMenuOpen, setFacilityMenuOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [addSellerDialogOpen, setAddSellerDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [savingSeller, setSavingSeller] = useState(false);
  const [newSellerData, setNewSellerData] = useState({
    displayName: "",
    email: "",
    phoneNumber: "",
    role: "seller",
  });
  const [editData, setEditData] = useState({
    branchName: "",
    adminName: "",
    phoneNumber: "",
    email: "",
    region: "",
    status: "Active",
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [tablePage, setTablePage] = useState(0);
  const ROWS_PER_PAGE = 5;

  useEffect(() => {
    setTablePage(0);
  }, [activeTab, searchQuery, statusFilter, roleFilter]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user?.email) return;

      try {
        const profileQuery = query(
          collection(firestore, "UserProfileMaster"),
          where("email", "==", user.email)
        );
        const profileSnapshot = await getDocs(profileQuery);

        if (!profileSnapshot.empty) {
          const profileDoc = profileSnapshot.docs[0];
          const profileData = profileDoc.data();
          const resolvedTenantId = profileData.tenantId || "";
          setTenantId(resolvedTenantId);
          setCurrentFacilityId(profileDoc.id);
          const sourceLabel = profileData.displayName || profileData.adminName || user.email || "F";
          setProfileLabel(sourceLabel.charAt(0).toUpperCase());
          setProfileEmail(profileData.email || user.email || "");
          setProfileRole(profileData.role || "facility_admin");
          setProfileBranchName(profileData.branchName || profileData.facilityName || "-");
          setProfileRegion(profileData.region || "-");
        }
      } catch (error) {
        console.error("Error loading facility admin profile:", error);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!tenantId) {
      setFacilityUsers([]);
      return;
    }

    const usersQuery = query(
      collection(firestore, "UserProfileMaster"),
      where("tenantId", "==", tenantId)
    );

    const unsubscribe = onSnapshot(usersQuery, (snapshot) => {
      setFacilityUsers(
        snapshot.docs.map((entry) => ({
          id: entry.id,
          ...entry.data(),
        }))
      );
    });

    return () => unsubscribe();
  }, [tenantId]);

  const associatedSellerEmails = useMemo(() => {
    const normalizeEmail = (value) =>
      String(value || "")
        .trim()
        .toLowerCase();

    const sellerRoles = new Set(["seller", "operator", "facility_operator", "shop"]);

    const emails = facilityUsers
      .filter((user) => {
        const normalizedRole = String(user.role || "").trim().toLowerCase();
        return user.facilityId === currentFacilityId && sellerRoles.has(normalizedRole);
      })
      .map((user) => normalizeEmail(user.email))
      .filter(Boolean);

    return Array.from(new Set(emails));
  }, [currentFacilityId, facilityUsers]);

  useEffect(() => {
    if (!associatedSellerEmails.length) {
      setComplaints([]);
      return;
    }

    const normalizeEmail = (value) =>
      String(value || "")
        .trim()
        .toLowerCase();

    const chunkValues = (values, chunkSize = 10) => {
      const chunks = [];
      for (let index = 0; index < values.length; index += chunkSize) {
        chunks.push(values.slice(index, index + chunkSize));
      }
      return chunks;
    };

    const sellerEmailSet = new Set(associatedSellerEmails);
    const emailChunks = chunkValues(Array.from(sellerEmailSet));
    const itemSnapshotCache = new Map();
    const complaintSnapshotCache = new Map();
    const itemComplaintUnsubscribers = new Map();
    const complaintItemUnsubscribers = new Map();
    const allUnsubscribers = [];

    let itemMap = new Map();
    let sellerItemIds = new Set();
    let currentItemChunkKey = "";
    let currentComplaintItemChunkKey = "";

    const rebuildItems = () => {
      const mergedItems = new Map();

      itemSnapshotCache.forEach((docsById) => {
        docsById.forEach((itemData, itemId) => {
          mergedItems.set(itemId, itemData);
        });
      });

      itemMap = mergedItems;
      sellerItemIds = new Set(Array.from(mergedItems.keys()));
    };

    const recomputeComplaints = () => {
      const complaintById = new Map();

      complaintSnapshotCache.forEach((docsById) => {
        docsById.forEach((complaintData, complaintId) => {
          complaintById.set(complaintId, complaintData);
        });
      });

      syncComplaintItemListeners(complaintById);

      const complaintList = Array.from(complaintById.entries())
        .filter(([, complaintData]) => {
          if (complaintData.isOrganisationComplaint === true) return false;

          if (tenantId) {
            const linkedItemTenantId = complaintData.itemId
              ? itemMap.get(complaintData.itemId)?.tenantId || ""
              : "";
            const complaintTenantId = complaintData.tenantId || linkedItemTenantId;

            if (complaintTenantId && complaintTenantId !== tenantId) {
              return false;
            }
          }

          const complaintSellerEmail = normalizeEmail(
            complaintData.sellerEmail || complaintData.shopEmail
          );

          if (complaintSellerEmail) {
            return sellerEmailSet.has(complaintSellerEmail);
          }

          return complaintData.itemId && sellerItemIds.has(complaintData.itemId);
        })
        .map(([complaintId, complaintData]) => {
          const itemData = itemMap.get(complaintData.itemId) || {};

          return {
            id: complaintId,
            complaintId: complaintData.complaintId || complaintId,
            raisedByEmail: complaintData.userEmail || complaintData.raisedByEmail || "-",
            make: itemData.itemMake || itemData.make || itemData.brand || complaintData.itemMake || complaintData.make || complaintData.brand || "-",
            model: itemData.model || itemData.modelName || complaintData.model || complaintData.modelName || "-",
            purchaseDate: formatDate(itemData.purchaseDate || itemData.purchaseDateTime || complaintData.purchaseDate || complaintData.purchaseDateTime),
            warrantyExpiryDate: formatDate(itemData.warrantyExpiryDate || complaintData.warrantyExpiryDate),
            issue: complaintData.issueDescription || complaintData.issue || "-",
            status: normalizeComplaintStatus(complaintData.status),
          };
        });

      setComplaints(complaintList);
    };

    const upsertSnapshotCache = (cache, key, snapshot) => {
      cache.set(
        key,
        new Map(
          snapshot.docs.map((snapshotDoc) => [snapshotDoc.id, snapshotDoc.data()])
        )
      );
    };

    const syncComplaintItemListeners = (complaintById) => {
      const complaintItemIds = Array.from(
        new Set(
          Array.from(complaintById.values())
            .map((complaintData) => complaintData.itemId)
            .filter(Boolean)
        )
      );

      const itemIdChunks = chunkValues(complaintItemIds);
      const nextChunkKey = JSON.stringify(itemIdChunks);

      if (nextChunkKey === currentComplaintItemChunkKey) {
        return;
      }

      complaintItemUnsubscribers.forEach((unsubscribe, key) => {
        unsubscribe();
        itemSnapshotCache.delete(key);
      });
      complaintItemUnsubscribers.clear();
      currentComplaintItemChunkKey = nextChunkKey;

      itemIdChunks.forEach((itemIdChunk, chunkIndex) => {
        const cacheKey = `item:complaintItemId:${chunkIndex}`;
        const unsubscribe = onSnapshot(
          query(collection(firestore, "ItemMaster"), where(documentId(), "in", itemIdChunk)),
          (snapshot) => {
            upsertSnapshotCache(itemSnapshotCache, cacheKey, snapshot);
            rebuildItems();
            recomputeComplaints();
          },
          (error) => {
            console.error("Error loading complaint items:", error);
            itemSnapshotCache.delete(cacheKey);
            rebuildItems();
            recomputeComplaints();
          }
        );

        complaintItemUnsubscribers.set(cacheKey, unsubscribe);
      });
    };

    const syncItemComplaintListeners = () => {
      const itemIdChunks = chunkValues(Array.from(sellerItemIds));
      const nextChunkKey = JSON.stringify(itemIdChunks);

      if (nextChunkKey === currentItemChunkKey) {
        recomputeComplaints();
        return;
      }

      itemComplaintUnsubscribers.forEach((unsubscribe, key) => {
        unsubscribe();
        complaintSnapshotCache.delete(key);
      });
      itemComplaintUnsubscribers.clear();
      currentItemChunkKey = nextChunkKey;

      itemIdChunks.forEach((itemIdChunk, chunkIndex) => {
        const cacheKey = `complaint:itemId:${chunkIndex}`;
        const unsubscribe = onSnapshot(
          query(collection(firestore, "Complaints"), where("itemId", "in", itemIdChunk)),
          (snapshot) => {
            upsertSnapshotCache(complaintSnapshotCache, cacheKey, snapshot);
            recomputeComplaints();
          },
          (error) => {
            console.error("Error loading facility complaints:", error);
            complaintSnapshotCache.delete(cacheKey);
            recomputeComplaints();
          }
        );

        itemComplaintUnsubscribers.set(cacheKey, unsubscribe);
      });

      recomputeComplaints();
    };

    emailChunks.forEach((emailChunk, chunkIndex) => {
      const shopItemKey = `item:shopEmail:${chunkIndex}`;
      const unsubscribeShopItems = onSnapshot(
        query(collection(firestore, "ItemMaster"), where("shopEmail", "in", emailChunk)),
        (snapshot) => {
          upsertSnapshotCache(itemSnapshotCache, shopItemKey, snapshot);
          rebuildItems();
          syncItemComplaintListeners();
        },
        (error) => {
          console.error("Error loading items for facility complaints:", error);
          itemSnapshotCache.delete(shopItemKey);
          rebuildItems();
          syncItemComplaintListeners();
        }
      );

      allUnsubscribers.push(unsubscribeShopItems);

      const sellerItemKey = `item:sellerEmail:${chunkIndex}`;
      const unsubscribeSellerItems = onSnapshot(
        query(collection(firestore, "ItemMaster"), where("sellerEmail", "in", emailChunk)),
        (snapshot) => {
          upsertSnapshotCache(itemSnapshotCache, sellerItemKey, snapshot);
          rebuildItems();
          syncItemComplaintListeners();
        },
        (error) => {
          console.error("Error loading items for facility complaints:", error);
          itemSnapshotCache.delete(sellerItemKey);
          rebuildItems();
          syncItemComplaintListeners();
        }
      );

      allUnsubscribers.push(unsubscribeSellerItems);

      const sellerComplaintKey = `complaint:sellerEmail:${chunkIndex}`;
      const unsubscribeSellerComplaints = onSnapshot(
        query(collection(firestore, "Complaints"), where("sellerEmail", "in", emailChunk)),
        (snapshot) => {
          upsertSnapshotCache(complaintSnapshotCache, sellerComplaintKey, snapshot);
          recomputeComplaints();
        },
        (error) => {
          console.error("Error loading facility complaints:", error);
          complaintSnapshotCache.delete(sellerComplaintKey);
          recomputeComplaints();
        }
      );

      allUnsubscribers.push(unsubscribeSellerComplaints);

      const shopComplaintKey = `complaint:shopEmail:${chunkIndex}`;
      const unsubscribeShopComplaints = onSnapshot(
        query(collection(firestore, "Complaints"), where("shopEmail", "in", emailChunk)),
        (snapshot) => {
          upsertSnapshotCache(complaintSnapshotCache, shopComplaintKey, snapshot);
          recomputeComplaints();
        },
        (error) => {
          console.error("Error loading facility complaints:", error);
          complaintSnapshotCache.delete(shopComplaintKey);
          recomputeComplaints();
        }
      );

      allUnsubscribers.push(unsubscribeShopComplaints);
    });

    return () => {
      allUnsubscribers.forEach((unsubscribe) => unsubscribe());
      itemComplaintUnsubscribers.forEach((unsubscribe) => unsubscribe());
      complaintItemUnsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [associatedSellerEmails, tenantId]);

  const filteredUsers = useMemo(() => {
    const roleScopedUsers = facilityUsers.filter((user) => {
      const normalizedRole = formatRole(user.role);

      if (normalizedRole === "Facility Admin") {
        return user.id === currentFacilityId;
      }

      if (normalizedRole === "Facility Operator" || normalizedRole === "Technician") {
        return user.facilityId === currentFacilityId;
      }

      return false;
    });

    return roleScopedUsers.filter((user) => {
      const userName = (user.adminName || user.displayName || "").toLowerCase();
      const userEmail = (user.email || "").toLowerCase();
      const matchesSearch =
        userName.includes(searchQuery.toLowerCase()) ||
        userEmail.includes(searchQuery.toLowerCase());
      const matchesRole =
        roleFilter === "All" ||
        (roleFilter === "Facility Operator" && formatRole(user.role) === "Facility Operator") ||
        (roleFilter === "Technician" && formatRole(user.role) === "Technician") ||
        (roleFilter === "Facility Admin" && formatRole(user.role) === "Facility Admin");
      const currentStatus = user.status || "Active";
      const matchesStatus = statusFilter === "All Status" || currentStatus === statusFilter;
      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [currentFacilityId, facilityUsers, roleFilter, searchQuery, statusFilter]);

  const filteredComplaints = useMemo(() => {
    return complaints.filter((entry) => {
      const searchValue = searchQuery.toLowerCase();
      const matchesSearch =
        entry.complaintId?.toLowerCase().includes(searchValue) ||
        entry.raisedByEmail?.toLowerCase().includes(searchValue) ||
        entry.issue?.toLowerCase().includes(searchValue);
      const matchesStatus =
        statusFilter === "All Status" ||
        normalizeComplaintStatus(entry.status) === normalizeComplaintStatus(statusFilter);
      return matchesSearch && matchesStatus;
    });
  }, [complaints, searchQuery, statusFilter]);

  const openComplaintsCount = useMemo(
    () => complaints.filter((entry) => !isComplaintClosed(entry.status)).length,
    [complaints]
  );

  const visibleSellerCount = useMemo(
    () => filteredUsers.filter((user) => formatRole(user.role) === "Facility Operator").length,
    [filteredUsers]
  );

  const visibleActiveSellerCount = useMemo(
    () =>
      filteredUsers.filter(
        (user) => formatRole(user.role) === "Facility Operator" && (user.status || "Active") === "Active"
      ).length,
    [filteredUsers]
  );

  const tabs = ["All", "Complaints"];

  const handleLogout = async () => {
    try {
      await signOut(auth);
      sessionStorage.removeItem("userRole");
      sessionStorage.removeItem("tenantId");
      navigate("/login");
    } catch (error) {
      console.error("Error while logging out:", error);
    }
  };

  const openProfileMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const closeProfileMenu = () => {
    setAnchorEl(null);
  };

  const openAddSellerDialog = () => {
    setNewSellerData({
      displayName: "",
      email: "",
      phoneNumber: "",
      role: "seller",
    });
    setAddSellerDialogOpen(true);
  };

  const closeAddSellerDialog = () => {
    if (savingSeller) return;
    setAddSellerDialogOpen(false);
  };

  const handleAddSellerFieldChange = (field) => (event) => {
    setNewSellerData((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleAddSeller = async () => {
    if (!tenantId || !currentFacilityId) {
      showSnackbar("Unable to identify tenant. Please try again.", "error");
      return;
    }

    if (!newSellerData.displayName?.trim() || !newSellerData.email?.trim()) {
      showSnackbar("Display Name and Email are required", "error");
      return;
    }

    setSavingSeller(true);
    try {
      const normalizedEmail = newSellerData.email.trim().toLowerCase();
      const normalizedRole = newSellerData.role === "Technician" ? "Technician" : "seller";
      const existingUserQuery = query(
        collection(firestore, "UserProfileMaster"),
        where("email", "==", normalizedEmail),
        where("tenantId", "==", tenantId)
      );
      const existingUserSnapshot = await getDocs(existingUserQuery);

      const sellerPayload = {
        displayName: newSellerData.displayName.trim(),
        adminName: newSellerData.displayName.trim(),
        email: normalizedEmail,
        phoneNumber: newSellerData.phoneNumber.trim(),
        role: normalizedRole,
        tenantId,
        facilityId: currentFacilityId,
        facilityName: profileBranchName || "",
        region: profileRegion || "",
        status: "Active",
        isActive: true,
        updatedAt: new Date(),
        updatedBy: auth.currentUser?.uid || null,
      };

      if (!existingUserSnapshot.empty) {
        const existingDoc = existingUserSnapshot.docs[0];
        const existingData = existingDoc.data();

        if (
          existingData.facilityId &&
          existingData.facilityId !== currentFacilityId &&
          isFacilityStaffRole(existingData.role)
        ) {
          showSnackbar("This user is already assigned to another facility.", "error");
          return;
        }

        if (!isFacilityStaffRole(existingData.role) && formatRole(existingData.role) !== "-") {
          showSnackbar("This email is already used by another non-facility user.", "error");
          return;
        }

        await updateDoc(doc(firestore, "UserProfileMaster", existingDoc.id), sellerPayload);
      } else {
        await addDoc(collection(firestore, "UserProfileMaster"), {
          ...sellerPayload,
          createdAt: new Date(),
          createdBy: auth.currentUser?.uid || null,
        });
      }

      showSnackbar(
        `${normalizedRole === "Technician" ? "Technician" : "Facility Operator"} added successfully`,
        "success"
      );
      setAddSellerDialogOpen(false);
    } catch (error) {
      console.error("Error adding seller:", error);
      showSnackbar("Adding facility operator was unsuccessful. Please try again.", "error");
    } finally {
      setSavingSeller(false);
    }
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    const facilityStaffUser = isFacilityStaffRole(user.role);
    setEditData({
      branchName: facilityStaffUser ? "" : user.branchName || "",
      adminName: user.adminName || user.displayName || "",
      phoneNumber: user.phoneNumber || "",
      email: user.email || "",
      region: facilityStaffUser ? "" : user.region || "",
      status: user.status || "Active",
    });
    setEditDialogOpen(true);
  };

  const closeEditDialog = () => {
    if (savingEdit) return;
    setEditDialogOpen(false);
    setSelectedUser(null);
  };

  const handleEditFieldChange = (field) => (event) => {
    setEditData((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSaveEdit = async () => {
    if (!selectedUser?.id) return;

    const editingFacilityStaff = isFacilityStaffRole(selectedUser.role);

    if (!editData.adminName?.trim() || !editData.phoneNumber?.trim() || !editData.email?.trim()) {
      showSnackbar("Name, phone number and email are required", "error");
      return;
    }

    if (!editingFacilityStaff && (!editData.branchName?.trim() || !editData.region?.trim())) {
      showSnackbar("Branch name and region are required for facility admin", "error");
      return;
    }

    setSavingEdit(true);
    try {
      const updatePayload = {
        adminName: editData.adminName.trim(),
        displayName: editData.adminName.trim(),
        phoneNumber: editData.phoneNumber.trim(),
        email: editData.email.trim().toLowerCase(),
        status: editData.status,
        isActive: editData.status === "Active",
        updatedAt: new Date(),
      };

      if (!editingFacilityStaff) {
        updatePayload.branchName = editData.branchName.trim();
        updatePayload.region = editData.region.trim();
      }

      await updateDoc(doc(firestore, "UserProfileMaster", selectedUser.id), updatePayload);
      showSnackbar(`${formatRole(selectedUser.role)} updated successfully`);
      setEditDialogOpen(false);
      setSelectedUser(null);
    } catch (error) {
      console.error("Error updating facility admin:", error);
      showSnackbar(
        `Failed to update ${formatRole(selectedUser.role).toLowerCase()}. Please try again.`,
        "error"
      );
    } finally {
      setSavingEdit(false);
    }
  };

  const openDeleteDialog = (user) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedUser(null);
  };

  const handleConfirmDelete = async () => {
    if (!selectedUser?.id) return;
    try {
      await deleteDoc(doc(firestore, "UserProfileMaster", selectedUser.id));
      showSnackbar(`${formatRole(selectedUser.role)} deleted successfully`);
      closeDeleteDialog();
    } catch (error) {
      console.error("Error deleting facility admin:", error);
      showSnackbar(`Failed to delete ${formatRole(selectedUser.role).toLowerCase()}. Please try again.`, "error");
    }
  };

  const stats = [
    {
      label: "Total Facility Operators",
      value: visibleSellerCount,
      sub: "+1 this month",
      icon: <GroupsIcon sx={{ color: "#5e35b1" }} />,
      tag: "All",
      tagColor: "#e3f2fd",
      tagTextColor: "#1976d2",
      tileColor: "rgba(94,53,177,0.08)",
      onClick: () => navigate("/facility-admin/sellers"),
    },
    {
      label: "Active Facility Operators",
      value: visibleActiveSellerCount,
      sub: `${Math.max(visibleSellerCount - visibleActiveSellerCount, 0)} inactive`,
      icon: <CheckCircleIcon sx={{ color: "#2e7d32" }} />,
      tag: "Active",
      tagColor: "#e8f5e9",
      tagTextColor: "#2e7d32",
      tileColor: "rgba(46,125,50,0.10)",
      onClick: () => navigate("/facility-admin/sellers?status=active"),
    },
    {
      label: "Open Complaints",
      value: openComplaintsCount,
      sub: `${openComplaintsCount} awaiting action`,
      icon: <AssignmentIcon sx={{ color: "#ef6c00" }} />,
      tag: "Open",
      tagColor: "#fff3e0",
      tagTextColor: "#ef6c00",
      tileColor: "rgba(239,108,0,0.10)",
      onClick: () => navigate("/complaints_list"),
    },
  ];

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
      <AppBar
        position="sticky"
        sx={{
          background: "rgba(254, 252, 232, 0.95)",
          color: "#1c1400",
          boxShadow: "none",
          borderBottom: "1px solid rgba(240, 208, 96, 0.25)",
          backdropFilter: "blur(12px)",
        }}
      >
        <Toolbar>
          <IconButton color="inherit" edge="start" sx={{ mr: 1 }} onClick={() => setDrawerOpen(true)}>
            <MenuIcon />
          </IconButton>
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 1.5, flexGrow: 1, cursor: "pointer" }}
            onClick={() => navigate("/")}
          >
            <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
              <Typography
                            variant="h6"
                            sx={{
                              fontFamily: "'Unbounded', cursive",
                              fontWeight: 900,
                              color: '#1c1400',
                              fontSize: '1.1rem',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              lineHeight: 1,
                            }}
                          >
                            Aalay<span style={{ color: '#f59e0b', fontSize: '1.4rem' }}>.</span>
                          </Typography>
              
                          <Typography
                            variant="caption"
                            component="div"
                            sx={{
                              fontStyle: 'italic',
                              color: '#1c1400',
                              opacity: 0.85,
                              fontSize: '0.7rem',
                              mt: 0.2,
                              fontFamily: 'inherit',
                              textTransform: 'none',
                            }}
                          >
                            Intelligent Operations by OmneVu.com
                          </Typography>
            </Box>
          </Box>
          <Avatar sx={{ bgcolor: "primary.main", color: "secondary.main", fontWeight: 700, cursor: "pointer" }} onClick={openProfileMenu}>
            {profileLabel}
          </Avatar>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={closeProfileMenu}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{ sx: { width: 300, mt: 1, borderRadius: 1.5 } }}
          >
            <Box sx={{ px: 3, py: 2 }}>
              <Typography variant="body1" sx={{ color: "text.secondary", wordBreak: "break-word", mb: 1.5 }}>
                {profileEmail || auth.currentUser?.email || "-"}
              </Typography>
              <Typography variant="body1" sx={{ color: "text.secondary" }}>
                Role: {formatRole(profileRole)}
              </Typography>
            </Box>
            <Divider />
            <MenuItem
              onClick={() => {
                closeProfileMenu();
                handleLogout();
              }}
              sx={{ py: 2 }}
            >
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 270, display: "flex", flexDirection: "column", height: "100%" }}>
          <List sx={{ flexGrow: 1, p: 1 }}>
            <ListItemButton
              onClick={() => {
                navigate("/facility-admin");
                setDrawerOpen(false);
              }}
            >
              <ListItemIcon>
                <img src={logo} alt="OV WMS" style={{ width: 28, height: 28, objectFit: "contain" }} />
              </ListItemIcon>
              <ListItemText primary="Home" primaryTypographyProps={{ fontWeight: 500 }} />
            </ListItemButton>
            <Divider sx={{ my: 1 }} />

            {/* Facility Management collapsible section */}
            <ListItemButton onClick={() => setFacilityMenuOpen((prev) => !prev)}>
              <ListItemIcon>
                <GroupsIcon />
              </ListItemIcon>
              <ListItemText primary="Facility Management" />
              {facilityMenuOpen ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            <Collapse in={facilityMenuOpen} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                <ListItemButton sx={{ pl: 4 }} onClick={() => { navigate("/facility-admin/sellers"); setDrawerOpen(false); }}>
                  <ListItemIcon>
                    <GroupsIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="All Users" />
                </ListItemButton>
                <ListItemButton sx={{ pl: 4 }} onClick={() => { navigate("/complaints_list"); setDrawerOpen(false); }}>
                  <ListItemIcon>
                    <ReportProblemIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Complaints" />
                </ListItemButton>
              </List>
            </Collapse>
          </List>

          <Box sx={{ p: 2 }}>
            <Divider />
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", mt: 1.5, display: "block", wordBreak: "break-word" }}
            >
              Logged in as: {profileEmail}<br />Role: Facility Admin
            </Typography>
            <ListItemButton
              onClick={handleLogout}
              sx={{
                mt: 1.5,
                backgroundColor: (theme) => `${theme.palette.error.light}40`,
                "&:hover": {
                  backgroundColor: (theme) => `${theme.palette.error.light}60`,
                },
                borderRadius: 1,
              }}
            >
              <ListItemIcon>
                <LogoutIcon color="error" />
              </ListItemIcon>
              <ListItemText primary="Logout" primaryTypographyProps={{ color: "error.main", fontWeight: 600 }} />
            </ListItemButton>
          </Box>
        </Box>
      </Drawer>

      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, color: "#1f2a44" }}>
          Facility Admin
        </Typography>
        <Typography variant="subtitle1" sx={{ color: "#5f6f8f", mb: 2 }}>
          Facility: {profileBranchName} | Region: {profileRegion}
        </Typography>

        <Grid container spacing={3} sx={{ mt: 1, mb: 2 }}>
          {stats.map((stat) => (
            <Grid item xs={12} sm={6} md={3} key={stat.label}>
              <Card
                onClick={stat.onClick || undefined}
                sx={{
                  borderRadius: 3,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                  cursor: stat.onClick ? "pointer" : "default",
                  transition: stat.onClick ? "transform 0.18s ease, box-shadow 0.18s ease" : "none",
                  "&:hover": stat.onClick
                    ? {
                        transform: "translateY(-2px)",
                        boxShadow: "0 10px 28px rgba(0,0,0,0.10)",
                      }
                    : undefined,
                }}
              >
                <CardContent>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                    <Box sx={{ p: 1.5, bgcolor: stat.tileColor, borderRadius: 2 }}>{stat.icon}</Box>
                    <Chip
                      size="small"
                      label={stat.tag}
                      sx={{
                        bgcolor: stat.tagColor,
                        color: stat.tagTextColor,
                        fontWeight: 700,
                      }}
                    />
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: "#1f2a44", lineHeight: 1.1 }}>
                    {stat.value}
                  </Typography>
                  <Typography variant="subtitle1" sx={{ mt: 1, fontWeight: 600, color: "#42526e" }}>
                    {stat.label}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#6b778c" }}>
                    {stat.sub}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, color: "#1f2a44" }}>
            AMC Management
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Paper
                onClick={() => navigate("/amc/schedule/new")}
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  cursor: "pointer",
                  transition: "all 0.18s ease",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  "&:hover": {
                    boxShadow: "0 10px 28px rgba(0,0,0,0.10)",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#1f2a44", mb: 0.5 }}>
                    Create Maintenance Schedule
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#6b778c" }}>
                    Set up a new Annual Maintenance Contract.
                  </Typography>
                </Box>
                <ArrowForwardIosIcon sx={{ color: "primary.main", fontSize: "1.5rem" }} />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                onClick={() => navigate("/amc/schedule/list")}
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  cursor: "pointer",
                  transition: "all 0.18s ease",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  "&:hover": {
                    boxShadow: "0 10px 28px rgba(0,0,0,0.10)",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#1f2a44", mb: 0.5 }}>
                    Manage Maintenance Schedules
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#6b778c" }}>
                    View, update, or track maintenance schedules.
                  </Typography>
                </Box>
                <ArrowForwardIosIcon sx={{ color: "primary.main", fontSize: "1.5rem" }} />
              </Paper>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<AddIcon />}
            onClick={openAddSellerDialog}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              px: 3,
              borderRadius: 2,
            }}
          >
            Add Facility User
          </Button>
        </Box>

        <Paper sx={{ borderRadius: 3, overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}>
          <Box sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}>
            <Tabs
              value={activeTab}
              onChange={(_event, nextValue) => setActiveTab(nextValue)}
              sx={{
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "1.05rem",
                },
              }}
            >
              {tabs.map((tabName) => (
                <Tab key={tabName} label={tabName} />
              ))}
            </Tabs>
          </Box>

          <Box sx={{ p: 3 }}>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  placeholder={activeTab === 1 ? "Search by complaint id, email or issue..." : "Search by name or email..."}
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                    sx: { borderRadius: 2, bgcolor: "#f8f9fb" },
                  }}
                />
              </Grid>
              {activeTab !== 1 && (
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <Select
                      value={roleFilter}
                      onChange={(event) => setRoleFilter(event.target.value)}
                      sx={{ borderRadius: 2, bgcolor: "#f8f9fb" }}
                    >
                      <MenuItem value="All">All</MenuItem>
                      <MenuItem value="Facility Operator">Facility Operator</MenuItem>
                      <MenuItem value="Technician">Technician</MenuItem>
                      <MenuItem value="Facility Admin">Facility Admin</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <Select
                    value={statusFilter}
                    onChange={(event) => setStatusFilter(event.target.value)}
                    sx={{ borderRadius: 2, bgcolor: "#f8f9fb" }}
                  >
                    <MenuItem value="All Status">All Status</MenuItem>
                    {activeTab === 1 ? (
                      ["Open", "In Progress", "Resolved"].map((status) => (
                        <MenuItem key={status} value={status}>{status}</MenuItem>
                      ))
                    ) : (
                      ["Active", "Inactive"].map((status) => (
                        <MenuItem key={status} value={status}>{status}</MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <TableContainer>
              {activeTab === 1 && (
                <Table>
                  <TableHead>
                    <TableRow sx={{ "& th": { color: "#6b778c", fontWeight: 700 } }}>
                      <TableCell>Complaint ID</TableCell>
                      <TableCell>Raised By (Email)</TableCell>
                      <TableCell>Make</TableCell>
                      <TableCell>Model</TableCell>
                      <TableCell>Purchase Date</TableCell>
                      <TableCell>Warranty Expiry Date</TableCell>
                      <TableCell>Issue</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="center">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredComplaints.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={9} align="center" sx={{ py: 4, color: "text.secondary" }}>
                          No complaints found.
                        </TableCell>
                      </TableRow>
                    )}

                    {filteredComplaints.slice(tablePage * ROWS_PER_PAGE, (tablePage + 1) * ROWS_PER_PAGE).map((complaint) => (
                      <TableRow key={complaint.id} sx={{ "&:hover": { bgcolor: "#fafbff" } }}>
                        <TableCell>{complaint.complaintId}</TableCell>
                        <TableCell>{complaint.raisedByEmail}</TableCell>
                        <TableCell>{complaint.make}</TableCell>
                        <TableCell>{complaint.model}</TableCell>
                        <TableCell>{complaint.purchaseDate}</TableCell>
                        <TableCell>{complaint.warrantyExpiryDate}</TableCell>
                        <TableCell>{complaint.issue}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={normalizeComplaintStatus(complaint.status)}
                            sx={{
                              fontWeight: 700,
                              ...getComplaintStatusChipStyle(complaint.status),
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {activeTab === 1 && (
                <TablePagination
                  component="div"
                  count={filteredComplaints.length}
                  page={tablePage}
                  onPageChange={(_event, newPage) => setTablePage(newPage)}
                  rowsPerPage={ROWS_PER_PAGE}
                  rowsPerPageOptions={[ROWS_PER_PAGE]}
                />
              )}
              {activeTab !== 1 && (
                <Table>
                  <TableHead>
                    <TableRow sx={{ "& th": { color: "#6b778c", fontWeight: 700 } }}>
                      <TableCell>NAME</TableCell>
                      <TableCell>ROLE</TableCell>
                      <TableCell>PHONE</TableCell>
                      <TableCell>STATUS</TableCell>
                      <TableCell>JOINED</TableCell>
                      <TableCell align="center">ACTIONS</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredUsers.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4, color: "text.secondary" }}>
                          No facility users found.
                        </TableCell>
                      </TableRow>
                    )}

                    {filteredUsers.slice(tablePage * ROWS_PER_PAGE, (tablePage + 1) * ROWS_PER_PAGE).map((user) => (
                      <TableRow key={user.id} sx={{ "&:hover": { bgcolor: "#fafbff" } }}>
                        <TableCell>
                          <Box>
                            <Typography sx={{ fontWeight: 700, color: "#1f2a44" }}>
                              {user.adminName || user.displayName || "-"}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "#6b778c" }}>
                              {user.email || "-"}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{formatRole(user.role)}</TableCell>
                        <TableCell>{user.phoneNumber || "-"}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={user.status || "Active"}
                            sx={{
                              bgcolor: (user.status || "Active") === "Active" ? "#e8f5e9" : "#f5f5f5",
                              color: (user.status || "Active") === "Active" ? "#2e7d32" : "#616161",
                              fontWeight: 700,
                            }}
                          />
                        </TableCell>
                        <TableCell>{formatCreatedAt(user.createdAt)}</TableCell>
                        <TableCell align="center">
                          <IconButton
                            color="primary"
                            size="small"
                            aria-label={isSellerRole(user.role) ? "Edit facility operator" : "Edit facility admin"}
                            onClick={() => openEditDialog(user)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            color="error"
                            size="small"
                            aria-label={isSellerRole(user.role) ? "Delete facility operator" : "Delete facility admin"}
                            onClick={() => openDeleteDialog(user)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {activeTab !== 1 && (
                <TablePagination
                  component="div"
                  count={filteredUsers.length}
                  page={tablePage}
                  onPageChange={(_event, newPage) => setTablePage(newPage)}
                  rowsPerPage={ROWS_PER_PAGE}
                  rowsPerPageOptions={[ROWS_PER_PAGE]}
                />
              )}
            </TableContainer>
          </Box>
        </Paper>

        <Dialog open={addSellerDialogOpen} onClose={closeAddSellerDialog} fullWidth maxWidth="sm">
          <DialogTitle>Add Facility User</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
              <FormControl fullWidth>
                <Select value={newSellerData.role} onChange={handleAddSellerFieldChange("role")}>
                  <MenuItem value="seller">Facility Operator</MenuItem>
                  <MenuItem value="Technician">Technician</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Display Name *"
                fullWidth
                value={newSellerData.displayName}
                onChange={handleAddSellerFieldChange("displayName")}
              />
              <TextField
                label="Email *"
                type="email"
                fullWidth
                value={newSellerData.email}
                onChange={handleAddSellerFieldChange("email")}
              />
              <TextField
                label="Phone Number"
                fullWidth
                value={newSellerData.phoneNumber}
                onChange={handleAddSellerFieldChange("phoneNumber")}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={closeAddSellerDialog} disabled={savingSeller}>Cancel</Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleAddSeller}
              disabled={savingSeller}
              sx={{
                textTransform: "none",
                borderRadius: 2,
              }}
            >
              {savingSeller ? "Adding..." : "Add"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={editDialogOpen} onClose={closeEditDialog} fullWidth maxWidth="sm">
          <DialogTitle>{`Edit ${formatRole(selectedUser?.role)}`}</DialogTitle>
          <DialogContent sx={{ pt: 2 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
              {!isFacilityStaffRole(selectedUser?.role) && (
                <TextField
                  label="Branch Name"
                  required
                  fullWidth
                  value={editData.branchName}
                  onChange={handleEditFieldChange("branchName")}
                />
              )}
              <TextField
                label={isFacilityStaffRole(selectedUser?.role) ? "User Name" : "Admin Name"}
                required
                fullWidth
                value={editData.adminName}
                onChange={handleEditFieldChange("adminName")}
              />
              <TextField
                label="Phone No"
                required
                fullWidth
                value={editData.phoneNumber}
                onChange={handleEditFieldChange("phoneNumber")}
              />
              <TextField
                label="Email"
                type="email"
                required
                fullWidth
                value={editData.email}
                onChange={handleEditFieldChange("email")}
              />
              {!isFacilityStaffRole(selectedUser?.role) && (
                <TextField
                  label="Region"
                  required
                  fullWidth
                  value={editData.region}
                  onChange={handleEditFieldChange("region")}
                />
              )}
              <FormControl fullWidth>
                <Select value={editData.status} onChange={handleEditFieldChange("status")}>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={closeEditDialog} disabled={savingEdit}>Cancel</Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleSaveEdit}
              disabled={savingEdit}
              sx={{
                textTransform: "none",
                borderRadius: 2,
              }}
            >
              {savingEdit ? "Saving..." : "Save"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} maxWidth="xs" fullWidth>
          <DialogTitle>{`Delete ${formatRole(selectedUser?.role)}`}</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete {selectedUser?.adminName || selectedUser?.displayName || "this user"}?
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={closeDeleteDialog}>Cancel</Button>
            <Button color="error" variant="contained" onClick={handleConfirmDelete}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FacilityAdminPage;
